import React from 'react';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';

import colors from '@atom/styles/colors';
import { ConversationalAnalyticsChartProps } from '@atom/types/conversationalAnalytics';
import {
  CHART_HEIGHT,
  CHART_MARGINS,
} from '@atom/utilities/conversationalAnalyticsUtilities';

import useConversationalAnalyticsData from './hooks/useConversationalAnalyticsData';
import ConversationalAnalyticsChartAxes from './ConversationalAnalyticsChartAxes';
import ConversationalAnalyticsChartWrapper from './ConversationalAnalyticsChartWrapper';

const isHorizontalBarChart = (
  spec: ConversationalAnalyticsChartProps['spec'],
) => {
  const { x, y } = spec.encoding;

  const isQuantitative = x?.type === 'quantitative';
  const isNominalOrOrdinal = ['nominal', 'ordinal'].includes(y?.type);

  return isQuantitative && isNominalOrOrdinal;
};

const ConversationalAnalyticsBarChart: React.FC<
  ConversationalAnalyticsChartProps
> = ({ spec, width = '100%', height, onExport }) => {
  const { chartData, xField, yField } = useConversationalAnalyticsData(spec);
  const isHorizontal = isHorizontalBarChart(spec);

  const barDataKey = isHorizontal ? xField : yField;

  return (
    <ConversationalAnalyticsChartWrapper spec={spec} onExport={onExport}>
      <ResponsiveContainer width={width} height={height || CHART_HEIGHT}>
        <BarChart
          data={chartData}
          margin={CHART_MARGINS}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
        >
          <ConversationalAnalyticsChartAxes
            spec={spec}
            isHorizontal={isHorizontal}
          />
          <Bar
            dataKey={barDataKey}
            fill={colors.brand.blue}
            radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ConversationalAnalyticsChartWrapper>
  );
};

export default ConversationalAnalyticsBarChart;
